import type { Answer, FieldDefinition, FieldId } from '@inside-us-aos/domain';
import {
  evaluateRuleExpression,
  isFieldRequired,
} from '@inside-us-aos/domain';

/**
 * Visibility / required evaluation pipeline.
 *
 * The form engine asks two questions of every catalog entry on each
 * recompute:
 *   1. Is this field currently visible? (governs UI rendering.)
 *   2. Is this field currently required? (governs validation +
 *      progress.)
 *
 * Both questions reduce to evaluating a `RuleExpression` against the
 * current answer set. The domain package provides the per-field
 * primitives (`evaluateRuleExpression`, `isFieldRequired`); this
 * module batches them across the catalog and returns the resulting
 * `Set<FieldId>` snapshots.
 *
 * A field with no `visibility` rule is considered always visible
 * (matches the catalog's implicit contract — fields without a
 * `visibility` clause are unconditionally rendered). Required is the
 * intersection of visibility and `isFieldRequired`: an invisible
 * field is never required regardless of its `required` flag, because
 * the user has no path to satisfy it.
 *
 * Memoization: the helpers walk the catalog once per call. Callers
 * that need cross-call caching should memoize at the engine layer
 * (Slice 7's state container is the natural home). Caching here
 * would risk staleness across catalog reloads.
 */

const isFieldVisible = (
  field: FieldDefinition,
  answers: readonly Answer[],
): boolean =>
  field.visibility ? evaluateRuleExpression(field.visibility, answers) : true;

/**
 * Returns the set of field IDs whose `visibility` rule (if any)
 * evaluates to true given the current answers. Fields without a
 * visibility rule are always included.
 */
export const resolveVisibleFieldIds = (
  catalog: readonly FieldDefinition[],
  answers: readonly Answer[] = [],
): ReadonlySet<FieldId> => {
  const visible = new Set<FieldId>();
  for (const field of catalog) {
    if (isFieldVisible(field, answers)) {
      visible.add(field.id);
    }
  }
  return visible;
};

/**
 * Returns the set of field IDs that are both visible and required
 * given the current answers. Required-but-invisible fields are
 * excluded — the user cannot satisfy a field they cannot see.
 */
export const resolveRequiredFieldIds = (
  catalog: readonly FieldDefinition[],
  answers: readonly Answer[] = [],
): ReadonlySet<FieldId> => {
  const required = new Set<FieldId>();
  for (const field of catalog) {
    if (!isFieldVisible(field, answers)) continue;
    if (isFieldRequired(field, answers)) {
      required.add(field.id);
    }
  }
  return required;
};

/**
 * Per-field status snapshot. Useful for UI components that need to
 * know "is this one field visible / required right now?" without
 * recomputing the full catalog. Internally the helpers above are the
 * canonical batch API; this is a convenience over them.
 */
export interface FieldStatus {
  visible: boolean;
  required: boolean;
}

export const resolveFieldStatus = (
  field: FieldDefinition,
  answers: readonly Answer[] = [],
): FieldStatus => {
  const visible = isFieldVisible(field, answers);
  return {
    visible,
    required: visible && isFieldRequired(field, answers),
  };
};
